import React from 'react';
import FilterBase from './FilterBase';
import DateInput from 'components/connectedForm/DateInput/DateInput';
import * as DateHelper from 'utils/DateHelper';
import styles from 'styles/partials/_filterdate.css';
import Dropdown from '../connectedForm/Dropdown';
import fieldMetaData from '../../graphql/schema/fieldMetaData';
import CheckboxInput from '../connectedForm/CheckboxInput';
import getValue from '../connectedForm/utils/getValue';
import { BlurTextField } from '../connectedForm/inputComponents';

export default class FilterDateTime extends FilterBase {
  constructor(props) {
    super(props);

    this.getDescription = this.getDescription.bind(this);
    this.getQueryFilters = this.getQueryFilters.bind(this);
    this.handleCalendarMouseDown = this.handleCalendarMouseDown.bind(this);
    this.renderPopout = this.renderPopout.bind(this);
    this.handlePeriodChange = this.handlePeriodChange.bind(this);
  }

  getInitialValues(props) {
    let initialValues = Object.assign({}, props);
    if (!props.dateType) {
      initialValues.dateType = 'DATE_RANGE';
      initialValues.dateRelative = 'TODAY';
      initialValues.numberOfPeriods = 1;
      initialValues.financialCalendar = true;
    }
    delete initialValues.label;
    delete initialValues.type;
    delete initialValues.fieldName;
    delete initialValues.onFilterUpdated;
    delete initialValues.position;
    delete initialValues.dispatch;
    delete initialValues.list;
    delete initialValues.initiallyOpen;

    return initialValues;
  }

  getDescription() {
    let from = this.committedValues.from ? DateHelper.jsr310ToText(this.committedValues.from) : null;
    let to = this.committedValues.to ? DateHelper.jsr310ToText(this.committedValues.to) : null;
    if (from && to) {
      return from + '-' + to;
    }
    if (from) {
      return from + ' or later';
    }
    if (this.committedValues.to) {
      return 'Up to ' + to;
    }
    return 'All';
  }

  getQueryFilters() {
    let queryFilters = [];
    if (this.state.filterValues.from) {
      queryFilters.push({
        fieldName: this.props.fieldName,
        operation: '>=',
        value: this.state.filterValues.from,
      });
    }
    if (this.state.filterValues.to) {
      queryFilters.push({
        fieldName: this.props.fieldName,
        operation: '<=',
        value: this.state.filterValues.to,
        includeNull: !this.state.filterValues.from,
      });
    }

    return queryFilters;
  }

  // Record that focus is still inside this component when click occurs inside calendar
  // isInside is reset by FilterBase when handleClickOutside is subsequently called
  handleCalendarMouseDown(event) {
    this.isInside = true;
  }

  handleClickOutside(event) {
    // If click is flagged as being actually part of this component (e.g. calendar popover of date) then
    // return false to prevent context button being closed
    if (this.isInside || (event && event.target && event.target.className.indexOf('react-date') > -1)) {
      this.isInside = false;
      return false;
    }

    // Otherwise context button will close so perform exit handling
    if (this.handleFilterClosed) {
      this.handleFilterClosed();
    }
    return true;
  }

  handleTimePickerMouseDown = (event) => {
    this.isInside = true;
  };

  handlePeriodChange(value) {
    let filterValues = Object.assign({}, this.state.filterValues, {
      ['numberOfPeriods']: value,
    });
    this.setState({ filterValues: filterValues });
  }

  renderPopout() {
    const { showType } = this.props;

    let messages = this.checkValueRange();
    let type = this.state.filterValues.dateType;
    let showPeriod = type.startsWith('NEXT_X') || type.startsWith('LAST_X');
    let showRelative = type.startsWith('NEXT') || type.startsWith('LAST');

    return (
      <div className={styles['container']}>
        <div className={styles['dateContainer']}>
          {showType ? (
            <Dropdown
              autoFocus={true}
              id="dateType"
              setInputRef={this.setInputRef}
              label="Date Filters"
              onChange={(value) => this.handleChange('dateType', value)}
              enum={true}
              labelCols={4 / 12}
              inputCols={8 / 12}
              options={fieldMetaData.FilterDateType.enumValues}
              value={this.state.filterValues.dateType}
              menuContainerStyle={{ maxWidth: '160px' }}
            />
          ) : null}
          {showPeriod ? (
            <BlurTextField
              id="numberOfPeriods"
              label={`${type.startsWith('NEXT') ? 'Next x' : 'Last x'} ${type.substr(7, 8).toLowerCase()}`}
              onChange={(value) => this.handlePeriodChange(value)}
              labelCols={4 / 12}
              inputCols={8 / 12}
              decimals={0}
              textAlign="right"
              value={this.state.filterValues.numberOfPeriods}
              enableOnChange={true}
            />
          ) : null}
          {showRelative ? (
            <Dropdown
              id="dateRelative"
              label="Relative To"
              onChange={(value) => this.handleChange('dateRelative', value)}
              enum={true}
              labelCols={4 / 12}
              inputCols={8 / 12}
              options={fieldMetaData.FilterDateRelative.enumValues}
              value={this.state.filterValues.dateRelative}
              menuContainerStyle={{ maxWidth: '190px' }}
            />
          ) : null}
          {showRelative || type.endsWith('YEAR') || type.endsWith('QUARTER') ? (
            <CheckboxInput
              id="financialCalendar"
              label="Based on Financial Calendar"
              value={this.state.filterValues.financialCalendar}
              labelCols={10 / 12}
              inputCols={2 / 12}
              onChange={(ev) => this.handleChange('financialCalendar', ev.target.checked)}
            />
          ) : null}
          {type === 'DATE_RANGE' ? (
            <>
              <DateInput
                id="from"
                label="From"
                labelCols={3 / 12}
                inputCols={9 / 12}
                setInputRef={this.setInputRef}
                value={this.state.filterValues.from}
                portal={true}
                showTimeSelect={true}
                onChange={(value) => this.handleChange('from', value)}
                onCalendarMouseDown={this.handleCalendarMouseDown}
              />
              <DateInput
                id="to"
                label="To"
                labelCols={3 / 12}
                inputCols={9 / 12}
                value={this.state.filterValues.to}
                portal={true}
                showTimeSelect={true}
                onChange={(value) => this.handleChange('to', value)}
                onCalendarMouseDown={this.handleCalendarMouseDown}
                message={messages}
              />
            </>
          ) : null}
        </div>
      </div>
    );
  }
}

FilterDateTime.defaultProps = {
  showType: true,
};
